// DS — toasty (artboard 06).
//
// Dialog pyta o DECYZJĘ i blokuje; toast RAPORTUJE coś, co już się stało, i nigdy
// nie blokuje. Tło --stage, kropka stanu (zielona sukces / różowa błąd), jedna akcja
// najwyżej, ZAWSZE „×” (G3-19). Bez akcji znika po 5 s; z akcją zostaje do zamknięcia —
// cofnięcie, którego nie da się dosięgnąć, jest gorsze niż żadne — chyba że wołający
// sam poda `duration` (raport, nie cofnięcie: sesja 19, „Seria w kolejce” = 8 s).
// Każde odliczanie stoi pod kursorem i pod fokusem. Escape zamyka najmłodszy toast
// z akcją.
//
// Wejście/wyjście: ds/motion.css (`.toast` + `.is-leaving`), czas wyjścia z tokenu.

import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import Icon from '../media/Icon'
import { useT } from '../../lib/i18n'
import { leaveMs } from '../motion'

export type ToastTone = 'ok' | 'danger' | 'warn' | 'neutral'

export type ToastInput = {
  /**
   * Treść komunikatu. WĘZEŁ, nie łańcuch (paczka P1 C): toast zwrotu i obciążenia niesie
   * KWOTĘ („Ponowiono edit · -[moneta] 10”), a kwota jest elementem — moneta plus liczba,
   * ze słowem wyłącznie dla czytnika ekranu. Łańcuch dalej wolno podać i nic się dla
   * niego nie zmienia.
   */
  message: ReactNode
  tone?: ToastTone
  /** Jedna akcja (zwykle cofnięcie). Bez własnego `duration` wyłącza auto-znikanie. */
  action?: { label: string; onClick: () => void }
  /**
   * Cały komunikat prowadzi w jedno miejsce (runda 32-R5, `R5-12`: stuknięcie toastu
   * błędu otwiera okno z faktem zwrotu i drogą naprawy). To NIE jest `action`: toast
   * nie dostaje drugiego napisu, tylko sam tekst staje się celem — inaczej fakt
   * („Błąd analizy · +10 tokenów”) musiałby dzielić miejsce z etykietą przycisku.
   * Cel jest prawdziwym `<button>`, więc dociera do klawiatury i do czytnika.
   */
  onSelect?: () => void
  /**
   * Milisekundy do auto-znikania. Bez akcji domyślnie 5000; z akcją auto-znikanie
   * włącza się TYLKO wtedy, gdy wołający poda tę liczbę świadomie (sesja 19,
   * toast „Seria w kolejce” = 8 s). Odliczanie stoi, gdy kursor albo fokus jest
   * na toaście — akcji, której nie da się dosięgnąć, nie oferujemy.
   */
  duration?: number
}

type ToastItem = ToastInput & { id: number; leaving?: boolean }

type ToastApi = {
  toast: (input: ToastInput | string) => void
  dismiss: (id: number) => void
}

const ToastCtx = createContext<ToastApi>({ toast: () => {}, dismiss: () => {} })

/** Najwyżej trzy naraz; starsze wypadają zamiast piętrzyć się w ścianę. */
const MAX_VISIBLE = 3
const DEFAULT_MS = 5000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())
  // Kiedy odliczany toast ma zniknąć, i ile mu zostało, gdy odliczanie stoi.
  const deadlines = useRef(new Map<number, number>())
  const paused = useRef(new Map<number, number>())
  const t = useT()

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) { clearTimeout(timer); timers.current.delete(id) }
    deadlines.current.delete(id)
    paused.current.delete(id)
    const ms = leaveMs()
    if (ms <= 0) { setItems((prev) => prev.filter((x) => x.id !== id)); return }
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, leaving: true } : x)))
    setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), ms)
  }, [])

  /** Nastaw odliczanie na `ms` (start albo wznowienie po pauzie). */
  const arm = useCallback((id: number, ms: number) => {
    const running = timers.current.get(id)
    if (running) clearTimeout(running)
    deadlines.current.set(id, Date.now() + ms)
    timers.current.set(id, setTimeout(() => dismiss(id), ms))
  }, [dismiss])

  /** Kursor albo fokus na toaście: odliczanie stoi, reszta zostaje bez zmian. */
  const hold = useCallback((id: number) => {
    const running = timers.current.get(id)
    if (!running) return
    clearTimeout(running)
    timers.current.delete(id)
    paused.current.set(id, Math.max(0, (deadlines.current.get(id) ?? Date.now()) - Date.now()))
  }, [])

  const release = useCallback((id: number) => {
    const left = paused.current.get(id)
    if (left == null) return
    paused.current.delete(id)
    arm(id, left)
  }, [arm])

  const toast = useCallback((input: ToastInput | string) => {
    const item: ToastItem = typeof input === 'string' ? { id: 0, message: input } : { id: 0, ...input }
    item.id = nextId.current++
    setItems((prev) => [...prev, item].slice(-MAX_VISIBLE))
    // Z akcją odliczamy tylko wtedy, gdy wołający podał czas; bez akcji zawsze.
    const ms = item.duration ?? (item.action ? null : DEFAULT_MS)
    if (ms != null) arm(item.id, ms)
  }, [arm])

  useEffect(() => {
    const pending = timers.current
    return () => { pending.forEach(clearTimeout); pending.clear() }
  }, [])

  // Escape zamyka najmłodszy toast z akcją (ten, który nie zniknie sam).
  useEffect(() => {
    const sticky = [...items].reverse().find((x) => x.action && !x.leaving)
    if (!sticky) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') dismiss(sticky.id) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [items, dismiss])

  const api = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="toast-viewport" role="status" aria-live="polite" aria-atomic="false">
        {items.map((x) => {
          const tone = x.tone ?? 'neutral'
          // Znikający komunikat przestał być komunikatem: aria-hidden od startu wyjścia, żeby czytnik
          // nie czytał, a axe nie mierzył koloru klatki przejścia (polish 13, §A5.2).
          return (
            <div
              key={x.id}
              className={`toast tone-${tone}${x.leaving ? ' is-leaving' : ''}`}
              aria-hidden={x.leaving || undefined}
              onMouseEnter={() => hold(x.id)}
              onMouseLeave={() => release(x.id)}
              onFocusCapture={() => hold(x.id)}
              onBlurCapture={() => release(x.id)}
            >
              <span className="toast-dot" aria-hidden="true" />
              {x.onSelect
                ? (
                  <button
                    type="button"
                    className="toast-msg toast-msg--go"
                    onClick={() => { x.onSelect?.(); dismiss(x.id) }}
                    data-testid="toast-go"
                  >
                    {x.message}
                  </button>
                )
                : <span className="toast-msg">{x.message}</span>}
              {x.action && (
                <button type="button" className="toast-action" onClick={() => { x.action?.onClick(); dismiss(x.id) }}>
                  {x.action.label}
                </button>
              )}
              <button type="button" className="toast-close" onClick={() => dismiss(x.id)} aria-label={t('dialogs.close')}>
                <Icon name="X" size={16} />
              </button>
            </div>
          )
        })}
      </div>
    </ToastCtx.Provider>
  )
}

/**
 * `const { toast } = useToast()` → `toast('Szablon zapisany')` albo
 * `toast({ message, tone: 'ok', action: { label: 'Cofnij', onClick } })`.
 * Bezpieczne poza providerem (no-op).
 */
export function useToast(): ToastApi {
  return useContext(ToastCtx)
}
